// (c) chaehongee. Confidential and proprietary.

import * as Sentry from '@sentry/react'
import i18n from '../i18n'
import { applyBackendProfilingHeader, buildApiUrl } from './http'
import { getSupabaseClient } from './supabase'
import type { AgentProcessRequest, AgentToolAction } from 'models-ts/agent'

export type AgentSuggestion = {
    id: string
    label: string
    prompt: string
    intent?: string | null
    tags?: string[] | null
    confidence?: number | null
}

export type AgentSuggestionsRequest = {
    session_id: string
    input_text?: string | null
    selected_nodes?: string[] | null
    selected_lora_id?: string | null
}

export type AgentSuggestionsResponse = {
    recommendations: AgentSuggestion[]
}

async function getAuthHeaders(): Promise<Headers> {
    const headers = new Headers()
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/x-ndjson')
    headers.set('Cache-Control', 'no-cache')
    applyBackendProfilingHeader(headers)

    // Add locale header for backend localization
    headers.set('X-Knoll-Locale', i18n.language || 'en')

    // Add JWT token if user is authenticated
    try {
        const supabase = getSupabaseClient()
        const {
            data: { session },
        } = await supabase.auth.getSession()
        if (session?.access_token) {
            headers.set('Authorization', `Bearer ${session.access_token}`)
        }
    } catch (error) {
        // Fail silently - request will proceed without auth
        console.warn('Failed to get auth session for agent request:', error)
    }

    return headers
}

async function getJsonAuthHeaders(): Promise<Headers> {
    const headers = await getAuthHeaders()
    headers.set('Accept', 'application/json')
    return headers
}

// Streaming NDJSON client for /agent/process
export type AgentStreamEvent =
    | { type: 'text_start' }
    | { type: 'text'; content: string }
    | {
        type: 'context_usage'
        model: string
        input_tokens: number
        output_tokens: number
        output_token_limit: number
        context_window_limit: number
    }
    | { type: 'tool_use'; tool_name: string; tool_id: string }
    | { type: 'interaction_required'; tool_name: string; tool_id: string; ui_spec: { version: number; react_code: string; initial_parameters: Record<string, any> } }
    | { type: 'tool_result'; tool_name: string; node_id?: string; [k: string]: any }
    | { type: 'image'; image_node_id: string }
    | { type: 'preview'; image_url: string }
    | { type: 'status'; message: string; step?: string }
    | { type: 'done' }
    | { type: 'error'; error: string; error_type?: string }

export function streamAgentProcess(
    request: AgentProcessRequest,
    onEvent: (e: AgentStreamEvent) => void,
    abortSignal?: AbortSignal
): { cancel: () => void } {
    const controller = new AbortController()
    const signal = abortSignal ?? controller.signal
    const debugStream =
        typeof window !== 'undefined' &&
        window.localStorage.getItem('knoll_stream_debug') === '1'

        ; (async () => {
            if (debugStream) {
                console.log('[streamAgentProcess] debug enabled')
            }
            const headers = await getAuthHeaders()
            const res = await fetch(buildApiUrl('/agent/process'), {
                method: 'POST',
                headers,
                body: JSON.stringify(request),
                signal,
            })
            if (!res.ok) {
                if (res.status === 409) {
                    let detail = 'Session is currently processing another request'
                    try {
                        const body = await res.json()
                        if (body?.detail) detail = body.detail
                    } catch { /* ignore */ }
                    onEvent({ type: 'error', error: detail, error_type: 'session_busy' })
                } else {
                    onEvent({ type: 'error', error: `HTTP ${res.status}` })
                }
                if (debugStream) {
                    console.log('[streamAgentProcess] http_error status=', res.status)
                }
                return
            }
            const reader = res.body?.getReader()
            if (!reader) {
                onEvent({ type: 'error', error: 'No response body' })
                if (debugStream) {
                    console.log('[streamAgentProcess] no_reader')
                }
                return
            }
            const decoder = new TextDecoder()
            let buffer = ''
            let eventCount = 0
            let lastEventAt = debugStream ? performance.now() : 0
            let lastReadAt = debugStream ? performance.now() : 0
            let readCount = 0
            try {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break

                    if (debugStream) {
                        const now = performance.now()
                        const dt = now - lastReadAt
                        // Large gaps here suggest buffering/backpressure before JS sees bytes.
                        if (dt > 250) {
                            console.log(
                                '[streamAgentProcess] reader.read gap_ms=',
                                dt.toFixed(1)
                            )
                        }
                        lastReadAt = now
                        readCount += 1
                        if (readCount === 1) {
                            console.log(
                                '[streamAgentProcess] first_chunk bytes=',
                                value?.byteLength ?? 0
                            )
                        }
                    }

                    buffer += decoder.decode(value, { stream: true })
                    let idx: number
                    // process complete lines
                    while ((idx = buffer.indexOf('\n')) >= 0) {
                        const line = buffer.slice(0, idx).trim()
                        buffer = buffer.slice(idx + 1)
                        if (!line) continue
                        try {
                            const evt = JSON.parse(line)
                            if (debugStream) {
                                const beforeCb = performance.now()
                                const gap = beforeCb - lastEventAt
                                lastEventAt = beforeCb
                                eventCount += 1

                                // Large gaps here suggest buffering or slow server flush.
                                if (gap > 250) {
                                    console.log(
                                        '[streamAgentProcess] event gap_ms=',
                                        gap.toFixed(1),
                                        'type=',
                                        evt?.type
                                    )
                                }

                                onEvent(evt)

                                const cbMs = performance.now() - beforeCb
                                // Slow callback suggests UI/dispatch/render work is the limiter.
                                if (cbMs > 16) {
                                    console.log(
                                        '[streamAgentProcess] onEvent slow_ms=',
                                        cbMs.toFixed(1),
                                        'type=',
                                        evt?.type,
                                        'count=',
                                        eventCount
                                    )
                                }
                                if (eventCount % 100 === 0) {
                                    console.log(
                                        '[streamAgentProcess] events=',
                                        eventCount,
                                        'last_type=',
                                        evt?.type
                                    )
                                }
                            } else {
                                onEvent(evt)
                            }
                        } catch {
                            // ignore malformed line
                        }
                    }
                }
                // flush remaining
                const tail = buffer.trim()
                if (tail) {
                    try {
                        const evt = JSON.parse(tail)
                        onEvent(evt)
                    } catch { }
                }
                if (debugStream) {
                    console.log(
                        '[streamAgentProcess] stream_end events=',
                        eventCount,
                        'reads=',
                        readCount
                    )
                }
            } catch (err: any) {
                if (err?.name === 'AbortError') return
                Sentry.captureException(err, { tags: { stream_type: 'agent_process' } })
                onEvent({ type: 'error', error: err?.message ?? 'Stream error' })
                if (debugStream) {
                    console.log('[streamAgentProcess] stream_error', err)
                }
            }
        })()

    return {
        cancel: () => controller.abort(),
    }
}


// Streaming NDJSON client for /agent/tool (interactive)
export type AgentToolActionExtra = {
    tool_id?: string
    tool_name?: string
    selected_nodes?: string[] | null
}

export function streamAgentTool(
    payload: AgentToolAction & AgentToolActionExtra,
    onEvent: (e: AgentStreamEvent) => void,
    abortSignal?: AbortSignal
): { cancel: () => void } {
    const controller = new AbortController()
    const signal = abortSignal ?? controller.signal
    const debugStream =
        typeof window !== 'undefined' &&
        window.localStorage.getItem('knoll_stream_debug') === '1'

        ; (async () => {
            if (debugStream) {
                console.log('[streamAgentTool] debug enabled')
            }
            const headers = await getAuthHeaders()
            const res = await fetch(buildApiUrl('/agent/tool'), {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
                signal,
            })
            if (!res.ok) {
                onEvent({ type: 'error', error: `HTTP ${res.status}` })
                if (debugStream) {
                    console.log('[streamAgentTool] http_error status=', res.status)
                }
                return
            }
            const reader = res.body?.getReader()
            if (!reader) {
                onEvent({ type: 'error', error: 'No response body' })
                if (debugStream) {
                    console.log('[streamAgentTool] no_reader')
                }
                return
            }
            const decoder = new TextDecoder()
            let buffer = ''
            let eventCount = 0
            let lastEventAt = debugStream ? performance.now() : 0
            let lastReadAt = debugStream ? performance.now() : 0
            let readCount = 0
            try {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break

                    if (debugStream) {
                        const now = performance.now()
                        const dt = now - lastReadAt
                        if (dt > 250) {
                            console.log(
                                '[streamAgentTool] reader.read gap_ms=',
                                dt.toFixed(1)
                            )
                        }
                        lastReadAt = now
                        readCount += 1
                        if (readCount === 1) {
                            console.log(
                                '[streamAgentTool] first_chunk bytes=',
                                value?.byteLength ?? 0
                            )
                        }
                    }

                    buffer += decoder.decode(value, { stream: true })
                    let idx: number
                    while ((idx = buffer.indexOf('\n')) >= 0) {
                        const line = buffer.slice(0, idx).trim()
                        buffer = buffer.slice(idx + 1)
                        if (!line) continue
                        try {
                            const evt = JSON.parse(line)
                            if (debugStream) {
                                const beforeCb = performance.now()
                                const gap = beforeCb - lastEventAt
                                lastEventAt = beforeCb
                                eventCount += 1

                                if (gap > 250) {
                                    console.log(
                                        '[streamAgentTool] event gap_ms=',
                                        gap.toFixed(1),
                                        'type=',
                                        evt?.type
                                    )
                                }

                                onEvent(evt)

                                const cbMs = performance.now() - beforeCb
                                if (cbMs > 16) {
                                    console.log(
                                        '[streamAgentTool] onEvent slow_ms=',
                                        cbMs.toFixed(1),
                                        'type=',
                                        evt?.type,
                                        'count=',
                                        eventCount
                                    )
                                }
                                if (eventCount % 100 === 0) {
                                    console.log(
                                        '[streamAgentTool] events=',
                                        eventCount,
                                        'last_type=',
                                        evt?.type
                                    )
                                }
                            } else {
                                onEvent(evt)
                            }
                        } catch {
                            // ignore malformed line
                        }
                    }
                }
                const tail = buffer.trim()
                if (tail) {
                    try {
                        const evt = JSON.parse(tail)
                        onEvent(evt)
                    } catch { }
                }
                if (debugStream) {
                    console.log(
                        '[streamAgentTool] stream_end events=',
                        eventCount,
                        'reads=',
                        readCount
                    )
                }
            } catch (err: any) {
                if (err?.name === 'AbortError') return
                Sentry.captureException(err, { tags: { stream_type: 'agent_tool' } })
                onEvent({ type: 'error', error: err?.message ?? 'Stream error' })
                if (debugStream) {
                    console.log('[streamAgentTool] stream_error', err)
                }
            }
        })()

    return {
        cancel: () => controller.abort(),
    }
}


export async function getSessionProcessingStatus(
    sessionId: string
): Promise<{ session_id: string; is_processing: boolean }> {
    const headers = await getJsonAuthHeaders()
    const res = await fetch(buildApiUrl(`/agent/status/${sessionId}`), { headers })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
}


export async function fetchAgentSuggestions(
    request: AgentSuggestionsRequest,
    abortSignal?: AbortSignal
): Promise<AgentSuggestionsResponse> {
    const headers = await getJsonAuthHeaders()
    const res = await fetch(buildApiUrl('/agent/suggestions'), {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: abortSignal,
    })
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
    }
    return await res.json()
}


